<template>
    <div
        class="sign-in"
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <div class="sign-in__container">
            <div class="sign-in-description">
                <h2>{{ signinText }}</h2>
                <h5>{{ welcomeText }}</h5>
            </div>

            <div class="form-sign">
                <form
                    action="#"
                    class="form-sign__container"
                    @submit.prevent="onSubmit"
                >
                    <div class="form-sign__error">&nbsp;{{ ErrorText }}</div>
                    <div class="form-sign__item">
                        <input
                            id="name"
                            ref="nameElement"
                            v-model="nameValue"
                            :required="true"
                            class="form-sign__input active"
                            data-keydown="password"
                            :placeholder="enterUsernameText"
                            type="text"
                        >

                        <label class="form-sign__label" for="name">{{
                            usernameLabel
                        }}</label>
                    </div>

                    <div class="form-sign__item">
                        <input
                            id="password"
                            ref="password"
                            v-model="passwordValue"
                            class="form-sign__input"
                            data-keydown="submit"
                            data-keyup="name"
                            :placeholder="enterPasswordText"
                            required
                            type="password"
                        >

                        <label class="form-sign__label" for="name">{{
                            passwordText
                        }}</label>
                    </div>

                    <div class="form-sign__item"> 
                        <button
                            id="submit"
                            class="btn form-sign__btn"
                            data-keyup="password"
                            data-keydown="forgot"
                            type="submit"
                        >
                            {{ nextBtnText }}
                        </button>
                    </div>
                </form>

                <div class="form-sign__forgot" @click="goToForgot">
                    <button
                            id="forgot"
                            class="link"
                            data-keyup="submit"
                        >
                            {{ forgotPasswordText }}
                        </button>
                </div>

                <loader v-if="IsLoggingIn" class="loader"/>
            </div>
        </div>
        <custom-modal
            v-if="dialogModal.isShown"
            :title="dialogModal.title"
            :message="dialogModal.message"
            :buttons="dialogModal.buttons"
            :name="dialogModal.name"
            @on-btn-click="onModalClick"
        />
    </div>
</template>

<script setup lang="ts">
    import { useChannelsStore } from "@/stores/Channels";
    import { Strings } from "@/locales/types/enums/strings";
    import { publisher, AppError, AppErrorCode } from "@ca.vmedia.tv/domains";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { Logger } from '@/utils';
    import type { ICustomModalState } from '@/composables/globalModals/types'
    import { useLanguagesUtils } from "@/composables/useLanguagesUtils";
    import {onMounted, nextTick, type Ref, ref, onUnmounted, onBeforeUnmount} from "vue";
    import { useRouter } from 'vue-router';
    import { useI18n } from "vue-i18n";
    import { useAccountStore } from "@/stores/Account";
    import { useUserSettingsStore } from "@/stores/UserSettings";
    import { useClientMessagesStore } from "@/stores/ClientMessages";
    import { useGenresStore } from "@/stores/Genres";

    const { clearAllGenres } = useGenresStore();

    const { getChannels } = useChannelsStore();
    
    const ClientMessagesStore = useClientMessagesStore();
    const {
        startReceiving
    } = ClientMessagesStore;

    const AccountStoreModule = useAccountStore();
    const {
        Login,
        getErrorMessage,
        ClearLogin,
    } = AccountStoreModule;
    const {
        fetchSettings,
    } = useUserSettingsStore();
    
    const { t } = useI18n();
    const { $fire, $navigator } = useNuxtApp();
    const router = useRouter();
    const {mainPage} = useAppConfig();
    const { adjustLanguageSettings } = await useLanguagesUtils();
    const nameValue: Ref<string> = ref('');
    const passwordValue: Ref<string> = ref('');
    const IsLoggingIn: Ref<boolean> = ref(false);
    const isRegKeyListeners: Ref<boolean> = ref(false);
    const nameElement: Ref<HTMLInputElement | null> = ref(null);
    const ErrorText: Ref<string> = ref('');  // the error text to show above the username/password fields
    const traceLoginScreen = $fire.trace($fire.performance, 'LOG_IN_SCREEN');
    let isStartedTraceLoginScreen: boolean = false;
    const signinText: string = t(Strings.LOGIN_SIGNIN).toString();
    const enterUsernameText: string = t(Strings.LOGIN_ENTER_USERNAME).toString();
    const enterPasswordText: string = t(Strings.LOGIN_ENTER_PASSWORD).toString();
    const passwordText: string = t(Strings.LOGIN_PASSWORD).toString();
    const nextBtnText: string = t(Strings.LOGIN_NEXT).toString();
    const forgotPasswordText: string = t(Strings.LOGIN_FORGOT_PASSWORD).toString();
    const welcomeText = ((): string => {
        let appName: string = '';
        const name = `APP_NAME_${publisher}`;
        appName = t(Strings[name as keyof typeof Strings]).toString();

        return t(Strings.LOGIN_ENTER_CREDENTIALS, {publisher: appName}).toString();
    })();
    const usernameLabel = ((): string => {
        const loginUsername = `LOGIN_USERNAME_${publisher}`;
        const localizedString = t(Strings[loginUsername as keyof typeof Strings]).toString();
        return localizedString;
    })();
    const dialogModal: Ref<ICustomModalState> = ref({
        isShown: false,
        title: null,
        message: null,
        name: "signError",
        buttons: null
    });

    const getErrorTitle = (errorCode: string): string => {
        if (errorCode == AppErrorCode.SETTINGS_NO_SUBSCRIPTION_ERROR) {
            return t(Strings.LOGIN_ERR_NOT_AVAILABLE).toString();
        }
        return t(Strings.ERROR).toString();
    };
    const getErrorButton = (errorCode: string): string => errorCode == AppErrorCode.SETTINGS_NO_SUBSCRIPTION_ERROR ? t(Strings.LOGIN_SWITCH_ACCOUNT).toString() : t(Strings.OK_BUTTON).toString();
    const focusName = (): void => {
        if (nameElement.value) {
            nameElement.value.focus();
        }
    };
    const goToForgot = (): void => {
        void router.push(linkItems.logInForgot.routerPath);
    };
    const keyBackListener = (): void => {
        router.go(-1);
    };
    const loginAndLoadData = async (): Promise<void> => {
        await Login({
            username: nameValue.value,
            password: passwordValue.value,
        });
        await fetchSettings();
    };
    const loadChannelsAndMessages = async (): Promise<void> => {
        await getChannels();

        startReceiving().catch((error) => {
            Logger.captureError(error as Error);
        });
    };
    const onModalClick = (): void => {
        dialogModal.value.isShown = false;
        nameValue.value = '';
        passwordValue.value = '';
        focusName();
    };

    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = () => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:back", keyBackListener);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = () => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:back", keyBackListener);
            isRegKeyListeners.value = false;
        }
    };

    // start login request
    const onSubmit = async(): Promise<void> => {
        try {
            isStartedTraceLoginScreen = true;
            traceLoginScreen.start();
            Logger.captureLog('LOG_IN_SCREEN start');
        } catch (e) {
            Logger.captureLog(e);
        }

        IsLoggingIn.value = true;
        ErrorText.value = "";

        try {
            unregisterKeyListeners();
            await loginAndLoadData();
            await adjustLanguageSettings();
            await loadChannelsAndMessages();

            clearAllGenres();

            IsLoggingIn.value = false;
            await router.push(linkItems[mainPage].routerPath);
        } catch (err) {
            handleLoginError(err as AppError | Error);
        }
    };

    const handleLoginError = (err: AppError | Error) => {
        registerKeyListeners();
        IsLoggingIn.value = false;

        if (err instanceof AppError) {
            if (err.code === AppErrorCode.LOGIN_NOT_FOUND) {
                ErrorText.value = getErrorMessage(err.code);
            } else if (err.code !== null) {
                dialogModal.value = {
                    ...dialogModal.value,
                    isShown: true,
                    title: getErrorTitle(err.code),
                    message: getErrorMessage(err.code),
                    buttons: [getErrorButton(err.code)],
                }
            }

            ClearLogin();
        } else {
            Logger.captureLog(err);
        }
    };

    onMounted(() => {
       void nextTick((): void => {
            focusName();
            $navigator.disableLeftAndRightButtons();
        });
    });

    onUnmounted(() => {
        if (traceLoginScreen && isStartedTraceLoginScreen) {
            try {
                isStartedTraceLoginScreen = false;
                traceLoginScreen.stop();
                Logger.captureLog(`LOG_IN_SCREEN stop ${traceLoginScreen.durationUs / 1000000} sec`);
            } catch (e){
                Logger.captureLog(e);
            }
        }
    });

    onBeforeUnmount(() => {
        unregisterKeyListeners();
        $navigator.enableLeftAndRightButtons();
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
